import React from 'react';
import { Box, Collapse, IconButton, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { translate as t } from 'hooks/useTranslations';
import { neutral, primary } from '_lex/_foundations/phxColors';
import PhxBadge from '_lex/_atoms/PhxBadge';
import type { EisConfigScoringIndicator } from '../../_types';

interface ScoringFieldItemProps {
  indicator: EisConfigScoringIndicator;
  points: Record<string, number | null>;
  onScoreChange: (indicatorId: number, scoreKey: string, points: number | null) => void;
  onNumberPredicateChange: (indicatorId: number, field: 'operator' | 'value', value: string) => void;
  numberPredicate: { operator: string; value: string };
}

const NUMBER_OPERATORS = [
  { value: 'isEqualTo', label: 'is equal to' },
  { value: 'doesNotEqual', label: 'does not equal to' },
  { value: 'isGreaterThan', label: 'is greater than' },
  { value: 'isGreaterThanOrEqualTo', label: 'is greater than or equal to' },
  { value: 'isLessThan', label: 'is less than' },
  { value: 'isLessThanOrEqualTo', label: 'is less than or equal to' },
];

const POINTS_MIN = 1;
const POINTS_MAX = 100;

const PointsInput: React.FC<{
  value: number | null;
  onChange: (val: number | null) => void;
}> = ({ value, onChange }) => (
  <Stack
    direction='row'
    alignItems='center'
    spacing={1}
  >
    <TextField
      type='number'
      value={value ?? ''}
      onKeyDown={(e) => {
        if (['e', 'E', '-', '+', '.'].includes(e.key)) {
          e.preventDefault();
        }
      }}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(null);
          return;
        }
        if (!/^\d+$/.test(raw)) return;
        const parsed = parseInt(raw, 10);
        if (isNaN(parsed)) return;
        onChange(Math.min(POINTS_MAX, Math.max(POINTS_MIN, parsed)));
      }}
      inputProps={{ min: POINTS_MIN, max: POINTS_MAX, step: 1 }}
      size='small'
      sx={{
        padding: '0px',
        width: 96,
        '& .MuiInputBase-input': {
          textAlign: 'center',
          padding: '6px 8px',
          paddingTop: '6px',
        },
      }}
    />
    <Typography
      variant='bodyMd'
      color='text.secondary'
    >
      {t('performanceReporting.eis.configuration.manageScoring.points')}
    </Typography>
  </Stack>
);

const NumberFieldBody: React.FC<{
  points: number | null;
  predicate: { operator: string; value: string };
  onPredicateChange: (field: 'operator' | 'value', value: string) => void;
  onPointsChange: (points: number | null) => void;
}> = ({ points, predicate, onPredicateChange, onPointsChange }) => (
  <Paper
    variant='outlined'
    sx={{
      borderColor: neutral[200],
      borderRadius: 1.5,
      p: 3,
    }}
  >
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={1.5}
        flexWrap='wrap'
        gap={1}
      >
        <Typography
          variant='bodyMd'
          color='text.secondary'
          sx={{ flexShrink: 0 }}
        >
          {t('performanceReporting.eis.configuration.manageScoring.ifValue')}
        </Typography>

        <Select
          value={predicate.operator}
          onChange={(e) => onPredicateChange('operator', e.target.value)}
          size='small'
          sx={{ minWidth: 200 }}
        >
          {NUMBER_OPERATORS.map((op) => (
            <MenuItem
              key={op.value}
              value={op.value}
            >
              {t(`performanceReporting.eis.configuration.manageScoring.operator.${op.value}`) || op.label}
            </MenuItem>
          ))}
        </Select>

        <TextField
          type='number'
          value={predicate.value}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '' || /^-?\d+$/.test(raw)) {
              onPredicateChange('value', raw);
            }
          }}
          inputProps={{ step: 1 }}
          size='small'
          sx={{
            width: 100,
            paddingTop: '0px',
            '& .MuiInputBase-input': {
              padding: '6px',
              paddingTop: '6px',
            },
          }}
        />

        <Typography
          variant='bodyMd'
          color='text.secondary'
          sx={{ flexShrink: 0 }}
        >
          {t('performanceReporting.eis.configuration.manageScoring.thenAdd')}
        </Typography>
      </Stack>

      <PointsInput
        value={points}
        onChange={onPointsChange}
      />
    </Stack>
  </Paper>
);

const OptionRow: React.FC<{
  label: string;
  points: number | null;
  onPointsChange: (points: number | null) => void;
}> = ({ label, points, onPointsChange }) => (
  <Paper
    variant='outlined'
    sx={{
      borderColor: neutral[200],
      borderRadius: 1.5,
      p: 3,
    }}
  >
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
    >
      <Typography variant='bodyMd'>{label}</Typography>
      <PointsInput
        value={points}
        onChange={onPointsChange}
      />
    </Stack>
  </Paper>
);

const ScoringFieldItem: React.FC<ScoringFieldItemProps> = ({
  indicator,
  points,
  onScoreChange,
  onNumberPredicateChange,
  numberPredicate,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const status = indicator.status;

  const badgeColor = status === 'Scored' ? 'success' : status === 'PartiallyScored' ? 'warning' : 'default';

  const badgeLabel =
    status === 'Scored'
      ? t('performanceReporting.eis.configuration.manageScoring.status.scored')
      : status === 'PartiallyScored'
        ? t('performanceReporting.eis.configuration.manageScoring.status.partiallyScored')
        : t('performanceReporting.eis.configuration.manageScoring.status.notScored');

  const renderBody = () => {
    switch (indicator.fieldType) {
      case 'Number':
        return (
          <NumberFieldBody
            points={points['number'] ?? null}
            predicate={numberPredicate}
            onPredicateChange={(field, value) => onNumberPredicateChange(indicator.fieldId, field, value)}
            onPointsChange={(p) => onScoreChange(indicator.fieldId, 'number', p)}
          />
        );
      case 'Boolean':
        return (
          <Stack spacing={1}>
            <OptionRow
              label={t('performanceReporting.eis.configuration.manageScoring.boolean.true') || 'True'}
              points={points['true'] ?? null}
              onPointsChange={(p) => onScoreChange(indicator.fieldId, 'true', p)}
            />
            <OptionRow
              label={t('performanceReporting.eis.configuration.manageScoring.boolean.false') || 'False'}
              points={points['false'] ?? null}
              onPointsChange={(p) => onScoreChange(indicator.fieldId, 'false', p)}
            />
          </Stack>
        );
      case 'SingleSelect':
      case 'MultiSelect':
        return (
          <Stack spacing={1}>
            {(indicator.options ?? []).map((opt) => (
              <OptionRow
                key={opt.fieldOptionId}
                label={opt.label}
                points={points[String(opt.fieldOptionId)] ?? null}
                onPointsChange={(p) => onScoreChange(indicator.fieldId, String(opt.fieldOptionId), p)}
              />
            ))}
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Paper
      variant='outlined'
      sx={{
        borderColor: neutral[200],
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          py: 3,
          cursor: 'pointer',
          userSelect: 'none',
          bgcolor: expanded ? primary[50] : 'transparent',
          '&:hover': { bgcolor: expanded ? primary[100] : neutral[50] },
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
        >
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            aria-label={expanded ? 'Collapse field' : 'Expand field'}
          >
            {expanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
          </IconButton>
          <Typography variant='bodyMd'>{indicator.fieldName}</Typography>
        </Stack>

        <PhxBadge
          color={badgeColor}
          size='sm'
        >
          {badgeLabel}
        </PhxBadge>
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: neutral[200],
            p: 2,
            bgcolor: primary[50],
          }}
        >
          {renderBody()}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ScoringFieldItem;
